import { supabase } from "@amazing-care/supabase";
import {
  type Activity,
  type Announcement,
  type Department,
  type DocumentType,
  type DocumentVisibility,
  documentContentSchema,
  type Manager,
  type NewActivity,
  type NewDocument,
  type NewQuickLink,
  type QuickLink,
  type StaffDocument,
} from "@amazing-care/types";
import { FunctionsHttpError } from "@supabase/supabase-js";
import { sanitizeRichText } from "@/lib/rich-text";
import type { StaffDataSource, UploadProgress } from "./data-source.types";
import { uploadToSession } from "./sharepoint-upload";

/**
 * Live implementation over the schema in amazing-care-backend. RLS decides
 * what each call may read or write; these methods only translate between rows
 * and the shapes in `@amazing-care/types`. File documents go through the
 * `sharepoint` Edge Function instead of PostgREST.
 */

/** Rethrows a PostgREST error as-is, so `normalizeError` can map its code. */
function unwrap<T>(result: { data: T; error: unknown }): T {
  if (result.error) throw result.error;
  return result.data;
}

/** As `unwrap`, for queries that return rows. */
function unwrapRows<T>(result: { data: T[] | null; error: unknown }): T[] {
  return unwrap(result) ?? [];
}

const DEPARTMENT_COLUMNS =
  "id, name, description, icon_path, sort_order, sub_divisions(id, name, sort_order)";

type DepartmentRow = {
  id: string;
  name: string;
  description: string;
  icon_path: string | null;
  sub_divisions: { id: string; name: string; sort_order: number }[];
};

function toDepartment(row: DepartmentRow): Department {
  const subDivisions = [...row.sub_divisions]
    .sort((a, b) => a.sort_order - b.sort_order)
    .map(({ id, name }) => ({ id, name }));
  return {
    id: row.id,
    name: row.name,
    description: row.description,
    icon: row.icon_path ?? "",
    ...(subDivisions.length > 0 ? { subDivisions } : {}),
  };
}

const DOCUMENT_COLUMNS =
  "id, department_id, sub_division_id, name, category, doc_type, content, featured, visibility, created_at, updated_at, file_name, mime_type, size_bytes, sharepoint_web_url";

type DocumentRow = {
  id: string;
  department_id: string;
  sub_division_id: string | null;
  name: string;
  category: string;
  doc_type: DocumentType;
  content: unknown;
  featured: boolean;
  visibility: DocumentVisibility;
  created_at: string;
  updated_at: string;
  file_name: string | null;
  mime_type: string | null;
  size_bytes: number | null;
  sharepoint_web_url: string | null;
};

function toDocument(row: DocumentRow): StaffDocument {
  return {
    id: row.id,
    departmentId: row.department_id,
    ...(row.sub_division_id ? { subDivisionId: row.sub_division_id } : {}),
    name: row.name,
    category: row.category,
    type: row.doc_type,
    lastUpdated: row.updated_at,
    createdAt: row.created_at,
    // Validated, because the column is jsonb and the renderers trust its shape.
    content: documentContentSchema.parse(row.content),
    featured: row.featured,
    visibility: row.visibility,
    ...(row.file_name
      ? {
          file: {
            name: row.file_name,
            mimeType: row.mime_type ?? "application/octet-stream",
            sizeBytes: row.size_bytes ?? 0,
            ...(row.sharepoint_web_url ? { sharePointUrl: row.sharepoint_web_url } : {}),
          },
        }
      : {}),
  };
}

function inlineDocumentRow(input: NewDocument) {
  return {
    department_id: input.departmentId,
    sub_division_id: input.subDivisionId ?? null,
    name: input.name,
    category: input.category,
    doc_type: input.type,
    content: input.content,
    featured: input.featured ?? false,
    visibility: input.visibility ?? "everyone",
  };
}

function fileMetadata(input: NewDocument): string {
  return JSON.stringify({
    departmentId: input.departmentId,
    subDivisionId: input.subDivisionId ?? null,
    name: input.name,
    category: input.category,
    featured: input.featured ?? false,
    visibility: input.visibility ?? "everyone",
  });
}

/**
 * Calls the `sharepoint` Edge Function, which holds the Graph credentials.
 * Its error responses carry a message meant for the user; surface it.
 */
async function invokeSharePoint<T>(action: string, body: FormData | object): Promise<T> {
  const { data, error } = await supabase.functions.invoke<T>(`sharepoint/${action}`, { body });
  if (error) {
    if (error instanceof FunctionsHttpError) {
      const payload = await error.context.json().catch(() => null);
      if (payload && typeof payload.error === "string") throw new Error(payload.error);
    }
    throw error;
  }
  return data as T;
}

/**
 * Sends a file straight to SharePoint through an upload session the Edge
 * Function opens after its checks, so large files never pass through it.
 * Returns the id of the document the session is for.
 */
async function sendToSharePoint(
  target: { departmentId: string } | { documentId: string },
  input: NewDocument,
  file: File,
  onProgress?: UploadProgress
): Promise<string> {
  const { documentId, uploadUrl } = await invokeSharePoint<{
    documentId: string;
    uploadUrl: string;
  }>("upload-session", {
    ...target,
    fileName: file.name,
    size: file.size,
    visibility: input.visibility ?? "everyone",
  });
  await uploadToSession(uploadUrl, file, onProgress);
  return documentId;
}

async function getDocumentById(documentId: string): Promise<StaffDocument | null> {
  const row = unwrap(
    await supabase.from("documents").select(DOCUMENT_COLUMNS).eq("id", documentId).maybeSingle()
  );
  return row ? toDocument(row) : null;
}

async function requireDocument(documentId: string): Promise<StaffDocument> {
  const document = await getDocumentById(documentId);
  if (!document) throw new Error("That document no longer exists.");
  return document;
}

const ANNOUNCEMENT_COLUMNS = "id, title, description, published_at";

function toAnnouncement(row: {
  id: string;
  title: string;
  description: string;
  published_at: string;
}): Announcement {
  return {
    id: row.id,
    title: row.title,
    description: row.description,
    timestamp: row.published_at,
  };
}

const ACTIVITY_COLUMNS = "id, title, content, activity_date";

function toActivity(row: {
  id: string;
  title: string;
  content: string;
  activity_date: string;
}): Activity {
  return { id: row.id, title: row.title, content: row.content, date: row.activity_date };
}

function activityRow(input: NewActivity) {
  return {
    title: input.title,
    content: sanitizeRichText(input.content),
    // The column is a date; keep only the calendar day the admin picked.
    activity_date: input.date.slice(0, 10),
  };
}

const QUICK_LINK_COLUMNS =
  "id, label, department_id, sub_division_id, category, starts_on, ends_on";

function toQuickLink(row: {
  id: string;
  label: string;
  department_id: string;
  sub_division_id: string | null;
  category: string | null;
  starts_on: string | null;
  ends_on: string | null;
}): QuickLink {
  return {
    id: row.id,
    label: row.label,
    departmentId: row.department_id,
    ...(row.sub_division_id ? { subDivisionId: row.sub_division_id } : {}),
    ...(row.category ? { category: row.category } : {}),
    ...(row.starts_on ? { startsOn: row.starts_on } : {}),
    ...(row.ends_on ? { endsOn: row.ends_on } : {}),
  };
}

function quickLinkRow(input: NewQuickLink) {
  return {
    label: input.label,
    department_id: input.departmentId,
    sub_division_id: input.subDivisionId || null,
    category: input.category || null,
    starts_on: input.startsOn || null,
    ends_on: input.endsOn || null,
  };
}

/** The manager dashboard shows recent changes, not the whole history. */
const ACTIVITY_LOG_LIMIT = 50;

type ManagerDirectoryRow = {
  id: string | null;
  email: string | null;
  full_name: string | null;
  department_ids: string[] | null;
  sso_department_ids: string[] | null;
  pending: boolean | null;
};

function toManager(row: ManagerDirectoryRow): Manager {
  return {
    id: row.id ?? "",
    name: row.full_name || (row.email ?? ""),
    email: row.email ?? "",
    departmentIds: row.department_ids ?? [],
    ssoDepartmentIds: row.sso_department_ids ?? [],
    pending: row.pending ?? false,
  };
}

async function managerEmail(id: string): Promise<string> {
  const row = unwrap(
    await supabase.from("manager_directory").select("email").eq("id", id).maybeSingle()
  );
  if (!row?.email) throw new Error("That manager no longer exists.");
  return row.email;
}

export function createSupabaseDataSource(): StaffDataSource {
  return {
    async listDepartments() {
      const rows = unwrapRows(
        await supabase.from("departments").select(DEPARTMENT_COLUMNS).order("sort_order")
      );
      return rows.map(toDepartment);
    },

    async getDepartment(departmentId) {
      const row = unwrap(
        await supabase
          .from("departments")
          .select(DEPARTMENT_COLUMNS)
          .eq("id", departmentId)
          .maybeSingle()
      );
      return row ? toDepartment(row) : null;
    },

    async listDocuments() {
      const rows = unwrapRows(
        await supabase
          .from("documents")
          .select(DOCUMENT_COLUMNS)
          .order("updated_at", { ascending: false })
      );
      return rows.map(toDocument);
    },

    getDocument: getDocumentById,

    async addDocument(input, file, onProgress) {
      if (input.content.kind === "file") {
        if (!file) throw new Error("Choose a file to upload.");
        const documentId = await sendToSharePoint(
          { departmentId: input.departmentId },
          input,
          file,
          onProgress
        );
        const form = new FormData();
        form.set("documentId", documentId);
        form.set("uploadedFileName", file.name);
        form.set("metadata", fileMetadata(input));
        const { id } = await invokeSharePoint<{ id: string }>("upload", form);
        return requireDocument(id);
      }

      const row = unwrap(
        await supabase
          .from("documents")
          .insert(inlineDocumentRow(input))
          .select(DOCUMENT_COLUMNS)
          .single()
      );
      if (!row) throw { code: "42501" };
      return toDocument(row);
    },

    async updateDocument(documentId, input, file, onProgress) {
      const existing = await requireDocument(documentId);

      if (existing.content.kind === "file") {
        const form = new FormData();
        form.set("documentId", documentId);
        form.set("metadata", fileMetadata(input));
        if (file) {
          await sendToSharePoint({ documentId }, input, file, onProgress);
          form.set("uploadedFileName", file.name);
        }
        await invokeSharePoint("replace", form);
        return requireDocument(documentId);
      }

      if (input.content.kind === "file") {
        // Turning written content into an upload is a new document; the
        // dialog offers it that way.
        throw new Error("Upload the file as a new document instead.");
      }

      const rows = unwrapRows(
        await supabase
          .from("documents")
          .update(inlineDocumentRow(input))
          .eq("id", documentId)
          .select(DOCUMENT_COLUMNS)
      );
      // RLS turns a forbidden update into zero rows rather than an error.
      if (!rows[0]) throw { code: "42501" };
      return toDocument(rows[0]);
    },

    async deleteDocument(documentId) {
      const existing = await requireDocument(documentId);

      if (existing.content.kind === "file") {
        await invokeSharePoint("delete", { documentId });
        return;
      }

      const rows = unwrapRows(
        await supabase.from("documents").delete().eq("id", documentId).select("id")
      );
      if (!rows[0]) throw { code: "42501" };
    },

    async getDocumentFile(documentId) {
      return invokeSharePoint<Blob>("download", { documentId });
    },

    async getDocumentPreview(documentId) {
      return invokeSharePoint<Blob>("preview", { documentId });
    },

    async getDocumentLink(documentId) {
      const { url } = await invokeSharePoint<{ url: string }>("link", { documentId });
      return url;
    },
    async listAnnouncements() {
      const rows = unwrapRows(
        await supabase
          .from("announcements")
          .select(ANNOUNCEMENT_COLUMNS)
          .order("published_at", { ascending: false })
      );
      return rows.map(toAnnouncement);
    },

    async addAnnouncement(input) {
      const row = unwrap(
        await supabase
          .from("announcements")
          .insert({ title: input.title, description: input.description })
          .select(ANNOUNCEMENT_COLUMNS)
          .single()
      );
      if (!row) throw { code: "42501" };
      return toAnnouncement(row);
    },

    async updateAnnouncement(id, input) {
      const rows = unwrapRows(
        await supabase
          .from("announcements")
          .update({ title: input.title, description: input.description })
          .eq("id", id)
          .select(ANNOUNCEMENT_COLUMNS)
      );
      // RLS turns a forbidden update into zero rows rather than an error.
      if (!rows[0]) throw { code: "42501" };
      return toAnnouncement(rows[0]);
    },

    async deleteAnnouncement(id) {
      const rows = unwrapRows(
        await supabase.from("announcements").delete().eq("id", id).select("id")
      );
      if (!rows[0]) throw { code: "42501" };
    },

    async listReadAnnouncementIds() {
      // RLS returns only the caller's own receipts.
      const rows = unwrapRows(await supabase.from("announcement_reads").select("announcement_id"));
      return rows.map((row) => row.announcement_id);
    },

    async markAnnouncementRead(id) {
      unwrap(
        await supabase
          .from("announcement_reads")
          .upsert(
            { announcement_id: id },
            { onConflict: "profile_id,announcement_id", ignoreDuplicates: true }
          )
      );
    },

    async markAllAnnouncementsRead() {
      const announcements = unwrapRows(await supabase.from("announcements").select("id"));
      if (announcements.length === 0) return;
      unwrap(
        await supabase.from("announcement_reads").upsert(
          announcements.map((announcement) => ({ announcement_id: announcement.id })),
          { onConflict: "profile_id,announcement_id", ignoreDuplicates: true }
        )
      );
    },

    async listActivities() {
      const rows = unwrapRows(
        await supabase
          .from("activities")
          .select(ACTIVITY_COLUMNS)
          .order("activity_date", { ascending: false })
      );
      return rows.map(toActivity);
    },

    async getActivity(activityId) {
      const row = unwrap(
        await supabase
          .from("activities")
          .select(ACTIVITY_COLUMNS)
          .eq("id", activityId)
          .maybeSingle()
      );
      return row ? toActivity(row) : null;
    },

    async addActivity(input) {
      const row = unwrap(
        await supabase
          .from("activities")
          .insert(activityRow(input))
          .select(ACTIVITY_COLUMNS)
          .single()
      );
      if (!row) throw { code: "42501" };
      return toActivity(row);
    },

    async updateActivity(id, input) {
      const rows = unwrapRows(
        await supabase
          .from("activities")
          .update(activityRow(input))
          .eq("id", id)
          .select(ACTIVITY_COLUMNS)
      );
      if (!rows[0]) throw { code: "42501" };
      return toActivity(rows[0]);
    },

    async deleteActivity(id) {
      const rows = unwrapRows(await supabase.from("activities").delete().eq("id", id).select("id"));
      if (!rows[0]) throw { code: "42501" };
    },

    async listManagers() {
      const rows = unwrapRows(
        await supabase
          .from("manager_directory")
          .select("id, email, full_name, department_ids, sso_department_ids, pending")
          .order("full_name")
      );
      return rows.map(toManager);
    },

    async addManager(input) {
      unwrap(
        await supabase.rpc("admin_save_manager", {
          manager_email: input.email,
          manager_name: input.name,
          department_ids: input.departmentIds,
        })
      );
      // The directory row's id depends on whether this became an invite or
      // updated an existing member; callers refetch the list after saving.
      const email = input.email.trim().toLowerCase();
      return { ...input, id: `invite:${email}`, email };
    },

    async updateManager(id, input) {
      // The email is the key on the server, so it cannot change; the dialog
      // disables the field when editing.
      const email = await managerEmail(id);
      unwrap(
        await supabase.rpc("admin_save_manager", {
          manager_email: email,
          manager_name: input.name,
          department_ids: input.departmentIds,
        })
      );
      return { ...input, id, email };
    },

    async deleteManager(id) {
      unwrap(await supabase.rpc("admin_remove_manager", { manager_email: await managerEmail(id) }));
    },

    async listActivityLog() {
      const [entries, departments] = await Promise.all([
        supabase
          .from("activity_log")
          .select("id, actor_name, action, document_name, department_id, created_at")
          .order("created_at", { ascending: false })
          .limit(ACTIVITY_LOG_LIMIT),
        supabase.from("departments").select("id, name"),
      ]);
      // The log keeps the slug rather than a foreign key, so it outlives a
      // deleted department; resolve names here.
      const names = new Map(unwrapRows(departments).map((row) => [row.id, row.name]));
      return unwrapRows(entries).map((row) => ({
        id: row.id,
        actorName: row.actor_name || "Someone",
        action: row.action,
        documentName: row.document_name,
        departmentLabel:
          (row.department_id && names.get(row.department_id)) || row.department_id || "",
        timestamp: row.created_at,
      }));
    },

    async listQuickLinks() {
      const rows = unwrapRows(
        await supabase
          .from("quick_links")
          .select(QUICK_LINK_COLUMNS)
          .order("starts_on", { ascending: false, nullsFirst: false })
          .order("created_at", { ascending: false })
      );
      return rows.map(toQuickLink);
    },

    async addQuickLink(input) {
      const row = unwrap(
        await supabase
          .from("quick_links")
          .insert(quickLinkRow(input))
          .select(QUICK_LINK_COLUMNS)
          .single()
      );
      if (!row) throw { code: "42501" };
      return toQuickLink(row);
    },

    async updateQuickLink(id, input) {
      const rows = unwrapRows(
        await supabase
          .from("quick_links")
          .update(quickLinkRow(input))
          .eq("id", id)
          .select(QUICK_LINK_COLUMNS)
      );
      // RLS turns a forbidden update into zero rows rather than an error.
      if (!rows[0]) throw { code: "42501" };
      return toQuickLink(rows[0]);
    },

    async deleteQuickLink(id) {
      const rows = unwrapRows(
        await supabase.from("quick_links").delete().eq("id", id).select("id")
      );
      if (!rows[0]) throw { code: "42501" };
    },
  };
}
